import { useState } from 'react';
import {
  Callout,
  Chip,
  Empty,
  Field,
  Loading,
  Modal,
  Panel,
  Select,
  TableScroll,
  TextInput,
  explainFailure,
  formatDateTime,
  isApiClientError,
  useLoad,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { useAbilities } from '../abilities.tsx';
import { Failure } from '../shared.tsx';
import {
  MIN_REASON,
  affiliateTargets,
  cleanCode,
  codeRefusal,
  isWholeCode,
  brandAffiliateTargets,
  brandUserTargets,
  loginAsKinds,
  mayLogInAsAnyone,
  operatorChoices,
  pickerBlocker,
  staffTargets,
  startBody,
  type LoginAsKind,
  type LoginAsTarget,
  type PickerKind,
} from './login-as-model.ts';

export type { LoginAsKind } from './login-as-model.ts';

/**
 * Rule Book §3.13 — "Log in as" from AffDice SuperAdmin (owner decision 1, 2026-10-07).
 *
 * The button shows only to a platform user holding the capability for the
 * target's kind; the API checks it again. A reason is asked for, the API
 * mints a session in the target's own console, and the one-time link it
 * answers is opened in a new tab — never this tab, whose own session is
 * AffDice SuperAdmin's and stays here (3.10a).
 *
 * Two ways in: the row buttons (an operator's Team, an affiliate's profile),
 * which already know who, and "Log in as…" (this page's head, an operator's
 * actions panel), which asks — operator, then person, then reason. Both
 * start the session through the same `useStartLoginAs`.
 */

/** Spelled out, not built from the kind, so each key is looked up by name. */
const consoleName = (kind: LoginAsKind): string =>
  kind === 'AFFILIATE' ? t('integrity.loginAs.console.AFFILIATE')
    : kind === 'BRAND_USER' ? t('integrity.loginAs.console.BRAND_USER')
      : t('integrity.loginAs.console.OPERATOR_USER');
const kindName = (kind: PickerKind): string =>
  kind === 'SELF' ? t('integrity.loginAs.kind.SELF')
  : kind === 'AFFILIATE' ? t('integrity.loginAs.kind.AFFILIATE')
    : kind === 'BRAND_USER' ? t('integrity.loginAs.kind.BRAND_USER')
      : t('integrity.loginAs.kind.OPERATOR_USER');
const nobodyToPick = (kind: Exclude<PickerKind, 'SELF'>, onBrand: boolean): string =>
  onBrand && kind === 'AFFILIATE' ? t('integrity.loginAs.pick.brandNoneAFFILIATE')
  : onBrand && kind === 'BRAND_USER' ? t('integrity.loginAs.pick.brandNoneBRAND_USER')
  : kind === 'AFFILIATE' ? t('integrity.loginAs.pick.noneAFFILIATE')
    : kind === 'BRAND_USER' ? t('integrity.loginAs.pick.noneBRAND_USER')
      : t('integrity.loginAs.pick.noneOPERATOR_USER');

const CAPABILITY: Readonly<Record<LoginAsKind, string>> = {
  OPERATOR_USER: 'login_as.operator_user',
  BRAND_USER: 'login_as.brand_user',
  AFFILIATE: 'login_as.affiliate',
};

/** What this platform user may do, read once per page load and shared by every row. */
let mine: Promise<ReadonlySet<string>> | null = null;
export function myCapabilities(api: PlatformApi): Promise<ReadonlySet<string>> {
  mine ??= api
    .request<{ mine: string[] }>('GET', '/v1/platform/permissions')
    .then((m) => new Set(m.mine))
    .catch(() => {
      mine = null;
      return new Set<string>();
    });
  return mine;
}

export function useMayLogInAs(api: PlatformApi, kind: LoginAsKind): boolean {
  const caps = useLoad(() => myCapabilities(api), []);
  return caps.state === 'ready' && caps.value.has(CAPABILITY[kind]);
}

interface Started {
  readonly login_as_id: string;
  readonly handoff_url: string;
  readonly ends_at: string;
}

/** Starting a session — the one place both dialogs go through. */
function useStartLoginAs(api: PlatformApi, onStarted?: () => void) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [started, setStarted] = useState<StartedSession | null>(null);
  const [ending, setEnding] = useState(false);
  const [endFailure, setEndFailure] = useState<unknown>(null);
  const [ended, setEnded] = useState(false);

  /** `onCodeRefused` runs when the API refused the code itself, so the box can be emptied for the next one. */
  const start = (operatorId: string, body: Record<string, string>, onCodeRefused?: () => void) => {
    // Opened now, inside the click, so the browser treats it as the user's
    // own tab rather than a pop-up; it is pointed at the link once the API
    // answers, and closed again if it refuses.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setBusy(true);
    setFailure(null);
    api
      .request<Started>('POST', '/v1/platform/login-as', { query: { operator_id: operatorId }, body })
      .then((s) => {
        if (tab) tab.location.href = s.handoff_url;
        setStarted({
          loginAsId: s.login_as_id,
          operatorId,
          self: body['target_kind'] === 'SELF',
          ends: formatDateTime(s.ends_at),
          blockedUrl: tab ? null : s.handoff_url,
        });
        onStarted?.();
      })
      .catch((e: unknown) => {
        tab?.close();
        setFailure(e);
        if (codeRefusal(e) === 'code' || codeRefusal(e) === 'missing') onCodeRefused?.();
      })
      .finally(() => setBusy(false));
  };

  // Ends the session started above; the console's tab stops working at once.
  const end = () => {
    if (!started) return;
    setEnding(true);
    setEndFailure(null);
    api
      .request('POST', `/v1/platform/login-as/${started.loginAsId}/end`, {
        query: { operator_id: started.operatorId },
        body: {},
      })
      .then(() => setEnded(true))
      .catch(setEndFailure)
      .finally(() => setEnding(false));
  };
  return { busy, failure, started, start, end, ending, endFailure, ended };
}

interface StartedSession {
  readonly loginAsId: string;
  readonly operatorId: string;
  /** Entered as yourself: no end time is shown, End session is the way out. */
  readonly self: boolean;
  readonly ends: string;
  readonly blockedUrl: string | null;
}

/**
 * The start route's refusal, in this console's words when it is about the
 * code (wrong or used, missing, no authenticator app), else the API's own.
 */
function StartFailure({ failure }: { failure: unknown }) {
  const refusal = codeRefusal(failure);
  const text = refusal === 'code' ? t('integrity.loginAs.codeWrong')
    : refusal === 'missing' ? t('integrity.loginAs.codeMissing')
      : refusal === 'enrol' ? t('integrity.loginAs.codeEnrol')
        : explainFailure(failure).message;
  return <Callout tone="error">{text}</Callout>;
}

/**
 * The viewer's own authenticator code, asked for on every start (owner,
 * 2026-10-16): the same box sign-in asks for the second step with. Not drawn
 * once the API has said the viewer has no authenticator app, since no code
 * could then work; the failure above says what to do instead.
 */
function CodeField({ value, onChange, failure }: {
  value: string;
  onChange: (v: string) => void;
  failure: unknown;
}) {
  const refusal = codeRefusal(failure);
  if (refusal === 'enrol') return null;
  return (
    <Field label={t('integrity.loginAs.code')} hint={t('integrity.loginAs.codeHint')}>
      <TextInput
        value={value}
        onChange={(v) => onChange(cleanCode(v))}
        placeholder="000000"
        invalid={(refusal === 'code' || refusal === 'missing') && value === ''}
      />
    </Field>
  );
}

function StartedView({ session, onClose }: {
  session: ReturnType<typeof useStartLoginAs>;
  onClose: () => void;
}) {
  const { started, end, ending, endFailure, ended } = session;
  if (!started) return null;
  if (ended) {
    return (
      <>
        <Callout fold={false}>{t('integrity.loginAs.sessionEnded')}</Callout>
        <div className="form-actions">
          <button type="button" className="primary" onClick={onClose}>{t('integrity.loginAs.done')}</button>
        </div>
      </>
    );
  }
  return (
    <>
      <Callout fold={false}>
        {started.self
          ? t('integrity.loginAs.opened.self')
          : t('integrity.loginAs.opened', { ends: started.ends })}
      </Callout>
      {started.blockedUrl ? (
        <Callout tone="caution" fold={false}>
          {t('integrity.loginAs.blocked', { url: started.blockedUrl })}
        </Callout>
      ) : null}
      {endFailure ? <Callout tone="error">{explainFailure(endFailure).message}</Callout> : null}
      <div className="form-actions">
        <button type="button" className="copy" disabled={ending} onClick={onClose}>
          {t('integrity.loginAs.done')}
        </button>
        <button type="button" className="primary" disabled={ending} onClick={end}>
          {ending ? t('integrity.loginAs.ending') : t('integrity.loginAs.endSession')}
        </button>
      </div>
    </>
  );
}

export function LoginAsButton({
  api,
  operatorId,
  kind,
  name,
  targetUserId,
  affiliateId,
  brands,
}: {
  api: PlatformApi;
  operatorId: string;
  kind: LoginAsKind;
  /** Who, as the dialog's title names them. */
  name: string;
  targetUserId?: string;
  affiliateId?: string;
  /** A brand user's brands: one opens straight away, several are picked from. */
  brands?: readonly { id: string; name: string }[];
}) {
  const may = useMayLogInAs(api, kind);
  const [open, setOpen] = useState(false);
  if (!may) return null;
  return (
    <>
      <button type="button" className="copy" onClick={() => setOpen(true)}>
        {t('integrity.loginAs.action')}
      </button>
      {open ? (
        <LoginAsDialog
          api={api}
          operatorId={operatorId}
          kind={kind}
          name={name}
          {...(targetUserId ? { targetUserId } : {})}
          {...(affiliateId ? { affiliateId } : {})}
          {...(brands ? { brands } : {})}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function LoginAsDialog({
  api,
  operatorId,
  kind,
  name,
  targetUserId,
  affiliateId,
  brands,
  onClose,
}: {
  api: PlatformApi;
  operatorId: string;
  kind: LoginAsKind;
  name: string;
  targetUserId?: string;
  affiliateId?: string;
  brands?: readonly { id: string; name: string }[];
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [brandId, setBrandId] = useState(brands?.[0]?.id ?? '');
  const session = useStartLoginAs(api);
  const { busy, failure, started, start } = session;
  const tooShort = reason.trim().length < MIN_REASON;
  const noAuthenticator = codeRefusal(failure) === 'enrol';

  return (
    <Modal title={t('integrity.loginAs.title', { name })} onClose={() => { if (!busy) onClose(); }}>
      <div className="stack">
        {started ? <StartedView session={session} onClose={onClose} /> : (
          <>
            <Callout fold={false}>
              {t('integrity.loginAs.explain', { console: consoleName(kind) })}
            </Callout>
            {failure ? <StartFailure failure={failure} /> : null}
            {kind === 'BRAND_USER' && brands && brands.length > 1 ? (
              <Field label={t('integrity.loginAs.brand')}>
                <Select
                  value={brandId}
                  onChange={setBrandId}
                  options={brands.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            ) : null}
            <Field label={t('integrity.loginAs.reason')} hint={t('integrity.loginAs.reasonHint')}>
              <TextInput value={reason} onChange={setReason} autoFocus spellCheck autoCapitalize="sentences" />
            </Field>
            <CodeField value={code} onChange={setCode} failure={failure} />
            <div className="form-actions">
              <button type="button" className="copy" disabled={busy} onClick={onClose}>
                {t('integrity.loginAs.cancel')}
              </button>
              <button
                type="button"
                className="primary"
                disabled={busy || tooShort || !isWholeCode(code) || noAuthenticator}
                onClick={() => start(
                  operatorId,
                  startBody({ operatorId, kind, reason, code, targetUserId, affiliateId, brandId }),
                  () => setCode(''),
                )}
              >
                {busy ? t('integrity.loginAs.starting') : t('integrity.loginAs.start')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * "Log in as…" — pick the operator (unless the caller already has one), the
 * kind of person, the person, and say why. People come from the same reads
 * the operator popup uses (its Team and Affiliates lists), so each pick is a
 * cross-tenant read logged like any other.
 */
export function LoginAsPicker({
  api,
  operator,
  brand,
  onClose,
  onStarted,
}: {
  api: PlatformApi;
  /** Already chosen — the operator popup's actions panel passes its own. */
  operator?: { id: string; name: string } | undefined;
  /** A brand popup's own brand: only its brand users and affiliates are offered. */
  brand?: { id: string; name: string } | undefined;
  onClose: () => void;
  onStarted?: () => void;
}) {
  const { can } = useAbilities();
  const kinds = loginAsKinds(can, brand !== undefined);
  const [operatorId, setOperatorId] = useState(operator?.id ?? '');
  const [kind, setKind] = useState<PickerKind>(kinds[0] ?? 'OPERATOR_USER');
  const [targetId, setTargetId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const session = useStartLoginAs(api, onStarted);
  const { busy, failure, started, start } = session;
  const noAuthenticator = codeRefusal(failure) === 'enrol';

  const operators = useLoad(
    () => (operator ? Promise.resolve([]) : api.operators().then((r) => operatorChoices(r.items))),
    [],
  );
  const targets = useLoad<LoginAsTarget[] | null>(() => {
    if (!operatorId || kind === 'SELF') return Promise.resolve(null);
    if (kind === 'AFFILIATE') {
      return api.operatorAffiliates(operatorId).then((r) =>
        brand ? brandAffiliateTargets(r.items, brand.id) : affiliateTargets(r.items));
    }
    return api.operatorTeam(operatorId).then((r) =>
      brand && kind === 'BRAND_USER' ? brandUserTargets(r.items, brand) : staffTargets(r.items, kind));
  }, [operatorId, kind, brand?.id]);

  const list = targets.state === 'ready' ? targets.value ?? [] : [];
  const target = list.find((p) => p.id === targetId);
  const brands = kind === 'BRAND_USER' ? target?.brands ?? [] : [];
  const needsBrand = brands.length > 1;
  const blocker = pickerBlocker({ kind, operatorId, targetId, needsBrand, brandId, reason, code });

  const pickOperator = (id: string) => { setOperatorId(id); setTargetId(''); setBrandId(''); };
  const pickKind = (k: PickerKind) => { setKind(k); setTargetId(''); setBrandId(''); };
  const pickTarget = (id: string) => {
    setTargetId(id);
    const chosen = list.find((p) => p.id === id);
    setBrandId(chosen && chosen.brands.length === 1 ? chosen.brands[0]!.id : '');
  };

  const title = brand
    ? t('integrity.loginAs.pick.titleAt', { operator: brand.name })
    : operator
    ? t('integrity.loginAs.pick.titleAt', { operator: operator.name })
    : t('integrity.loginAs.pick.title');

  return (
    <Modal title={title} onClose={() => { if (!busy) onClose(); }} keepOnBackdropClick>
      <div className="stack">
        {started ? <StartedView session={session} onClose={onClose} /> : (
          <>
            {failure ? <StartFailure failure={failure} /> : null}

            {operator && kinds.length < 2 ? null : (
            <div className="form-row">
              {operator ? null : (
                <Field label={t('integrity.loginAs.colOperator')}>
                  {operators.state === 'loading' ? <Loading rows={1} /> : null}
                  {operators.state === 'error' ? <Failure error={operators.error} /> : null}
                  {operators.state === 'ready' ? (
                    operators.value.length === 0 ? (
                      <Callout tone="caution" fold={false}>{t('integrity.loginAs.pick.noOperators')}</Callout>
                    ) : (
                      <Select
                        value={operatorId}
                        onChange={pickOperator}
                        placeholder={t('integrity.loginAs.pick.chooseOperator')}
                        options={[
                          { value: '', label: t('integrity.loginAs.pick.chooseOperator'), disabled: true },
                          ...operators.value.map((o) => ({ value: o.id, label: o.name })),
                        ]}
                      />
                    )
                  ) : null}
                </Field>
              )}

              {kinds.length > 1 ? (
                <Field label={t('integrity.loginAs.pick.kind')}>
                  <Select
                    value={kind}
                    onChange={(v) => pickKind(v as PickerKind)}
                    searchable={false}
                    options={kinds.map((k) => ({ value: k, label: kindName(k) }))}
                  />
                </Field>
              ) : null}
            </div>
            )}

            {kind === 'SELF' ? (
              operatorId ? null : <p className="faint">{t('integrity.loginAs.pick.operatorFirst')}</p>
            ) : operatorId ? (
              <Field label={kindName(kind)}>
                {targets.state === 'loading' ? <Loading rows={1} /> : null}
                {targets.state === 'error' ? (
                  isApiClientError(targets.error) && targets.error.status === 403
                    ? <Callout tone="caution" fold={false}>{t('integrity.loginAs.pick.forbidden')}</Callout>
                    : <Failure error={targets.error} />
                ) : null}
                {targets.state === 'ready' ? (
                  list.length === 0 ? (
                    <Callout fold={false}>{nobodyToPick(kind, brand !== undefined)}</Callout>
                  ) : (
                    <Select
                      value={targetId}
                      onChange={pickTarget}
                      placeholder={t('integrity.loginAs.pick.choosePerson')}
                      options={[
                        { value: '', label: t('integrity.loginAs.pick.choosePerson'), disabled: true },
                        ...list.map((p) => ({
                          value: p.id,
                          label: p.detail && p.detail !== p.name ? `${p.name} — ${p.detail}` : p.name,
                        })),
                      ]}
                    />
                  )
                ) : null}
              </Field>
            ) : (
              <p className="faint">{t('integrity.loginAs.pick.operatorFirst')}</p>
            )}

            {needsBrand ? (
              <Field label={t('integrity.loginAs.brand')}>
                <Select
                  value={brandId}
                  onChange={setBrandId}
                  searchable={false}
                  options={[
                    { value: '', label: t('integrity.loginAs.pick.chooseBrand'), disabled: true },
                    ...brands.map((b) => ({ value: b.id, label: b.name })),
                  ]}
                />
              </Field>
            ) : null}

            {kind === 'SELF' ? (
              operatorId ? (
                <Callout>{brand ? t('integrity.loginAs.explain.selfBrand') : t('integrity.loginAs.explain.selfOperator')}</Callout>
              ) : null
            ) : target ? (
              <Callout>{t('integrity.loginAs.explain', { console: consoleName(kind) })}</Callout>
            ) : null}

            {kind === 'SELF' ? null : (
              <Field label={t('integrity.loginAs.reason')} hint={t('integrity.loginAs.reasonHint')}>
                <TextInput value={reason} onChange={setReason} spellCheck autoCapitalize="sentences" />
              </Field>
            )}

            {operatorId ? <CodeField value={code} onChange={setCode} failure={failure} /> : null}

            <div className="form-actions">
              <button type="button" className="copy" disabled={busy} onClick={onClose}>
                {t('integrity.loginAs.cancel')}
              </button>
              <button
                type="button"
                className="primary"
                disabled={busy || blocker !== null || noAuthenticator}
                onClick={() => {
                  const emptyCode = () => setCode('');
                  if (kind === 'SELF') {
                    start(operatorId, startBody({ operatorId, kind, reason: '', code, brandId: brand?.id }), emptyCode);
                    return;
                  }
                  const isAffiliate = kind === 'AFFILIATE';
                  start(operatorId, startBody({
                    operatorId,
                    kind,
                    reason,
                    code,
                    targetUserId: isAffiliate ? undefined : targetId,
                    affiliateId: isAffiliate ? targetId : undefined,
                    // A brand's picker opens an operator user locked to that brand too.
                    brandId: kind === 'OPERATOR_USER' ? brand?.id : brandId,
                  }), emptyCode);
                }}
              >
                {busy ? t('integrity.loginAs.starting') : t('integrity.loginAs.start')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** "Log in as…", for a page head or an actions panel: drawn only when the viewer may. */
export function LoginAsPickerButton({
  api,
  operator,
  brand,
  className = 'copy',
  onStarted,
}: {
  api: PlatformApi;
  operator?: { id: string; name: string } | undefined;
  /** Scopes the picker to one brand (a brand popup's actions panel). */
  brand?: { id: string; name: string } | undefined;
  className?: string;
  onStarted?: () => void;
}) {
  const { ready, can } = useAbilities();
  const [open, setOpen] = useState(false);
  if (!ready || !mayLogInAsAnyone(can)) return null;
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        {t('integrity.loginAs.pick.action')}
      </button>
      {open ? (
        <LoginAsPicker
          api={api}
          operator={operator}
          brand={brand}
          onClose={() => setOpen(false)}
          {...(onStarted ? { onStarted } : {})}
        />
      ) : null}
    </>
  );
}

interface HistoryRow {
  readonly id: string;
  readonly operator_id: string;
  readonly operator: string;
  readonly platform_user: string;
  readonly target: string;
  /** SELF: the viewer's own live session in a console as themselves, listed only to them. */
  readonly target_kind: PickerKind;
  readonly reason: string | null;
  readonly state: 'ACTIVE' | 'ENDED' | 'EXPIRED';
  readonly started_at: string;
  readonly ends_at: string;
  readonly ended_at: string | null;
  readonly opened: boolean;
  readonly writes: readonly { method: string; route: string; status: number }[];
}

/** Integrity → Log in as: start one, and every session on every operator (3.13f). */
export function LoginAsHistory({ api }: { api: PlatformApi }) {
  const { ready, can } = useAbilities();
  const [version, setVersion] = useState(0);
  const [failure, setFailure] = useState<unknown>(null);
  const [ending, setEnding] = useState<string | null>(null);
  const loaded = useLoad(
    () => api.request<{ items: HistoryRow[] }>('GET', '/v1/platform/login-as'),
    [version],
  );
  const may = ready && mayLogInAsAnyone(can);

  const head = (
    <div className="page-head">
      <h1>{t('nav.loginAs')}</h1>
      {may ? (
        <div className="page-head-actions">
          <LoginAsPickerButton api={api} className="primary" onStarted={() => setVersion((v) => v + 1)} />
        </div>
      ) : null}
    </div>
  );
  if (loaded.state === 'loading') return <>{head}<Loading rows={3} /></>;
  if (loaded.state === 'error') return <>{head}<Failure error={loaded.error} /></>;
  const rows = loaded.value.items;

  const end = (r: HistoryRow) => {
    setEnding(r.id);
    setFailure(null);
    api
      .request('POST', `/v1/platform/login-as/${r.id}/end`, { query: { operator_id: r.operator_id }, body: {} })
      .then(() => setVersion((v) => v + 1))
      .catch(setFailure)
      .finally(() => setEnding(null));
  };

  return (
    <>
      {head}
      {failure ? <Failure error={failure} /> : null}
      <Panel title={t('integrity.loginAs.sessions')} aside={<span className="count count-badge">{rows.length}</span>}>
        {rows.length === 0 ? (
          <Empty>{may ? t('integrity.loginAs.noneStart') : t('integrity.loginAs.noneNoRight')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('integrity.loginAs.colStarted')}</th>
                  <th>{t('integrity.loginAs.colWho')}</th>
                  <th>{t('integrity.loginAs.colAs')}</th>
                  <th>{t('integrity.loginAs.colOperator')}</th>
                  <th>{t('integrity.loginAs.colReason')}</th>
                  <th>{t('integrity.loginAs.colState')}</th>
                  <th>{t('integrity.loginAs.colDone')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="num faint">{formatDateTime(r.started_at)}</td>
                    <td>{r.platform_user}</td>
                    <td>
                      {r.target}
                      <div className="faint">{kindName(r.target_kind)}</div>
                    </td>
                    <td className="faint">{r.operator}</td>
                    <td className="wrap">{r.reason ?? '—'}</td>
                    <td>
                      {r.state === 'ACTIVE' ? (
                        <>
                          <Chip tone="attention">{t('integrity.loginAs.active', { ends: formatDateTime(r.ends_at) })}</Chip>{' '}
                          <button type="button" className="danger-quiet" disabled={ending === r.id} onClick={() => end(r)}>
                            {t('integrity.loginAs.end')}
                          </button>
                        </>
                      ) : r.state === 'EXPIRED' ? (
                        <span className="faint">{t('integrity.loginAs.expired')}</span>
                      ) : (
                        <span className="faint">{t('integrity.loginAs.ended', { at: formatDateTime(r.ended_at ?? r.ends_at) })}</span>
                      )}
                      {!r.opened ? <div className="faint">{t('integrity.loginAs.notOpened')}</div> : null}
                    </td>
                    <td className="wrap">
                      {r.writes.length === 0 ? '—' : r.writes.map((w, i) => (
                        <div key={i}>
                          <code className="faint">{w.method} {w.route}</code>
                          {w.status >= 400 ? <>{' '}<Chip tone="neutral">{w.status}</Chip></> : null}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>
    </>
  );
}
